// ──────────────────────────────────────────────────────
// TrustModal — "ضمان الأصالة" modal shown when the TrustBadge is tapped
// (or when the AI chat surfaces the CTA).
//
// Phase 1: catalog-global authenticity layer. The modal frames this as
// PLATFORM / SUPPLIER-backed authenticity proof — it does NOT claim
// per-barcode verification. The copy is product-relevant ("هذا المنتج
// مشمول ضمن نظام ضمان الأصالة") without implying per-SKU tracing.
//
// Phase 2 — DOM architecture fix:
//   The product cards (HeroCard / BudgetCard / ProductCardLegacy) are
//   wrapped in Next.js <Link>, which renders as <a>. TrustBadge is nested
//   inside that anchor tree. When the badge-triggered modal rendered its
//   per-document "عرض الوثيقة" <a href="…"> links inline, we ended up with
//   <a>…<a>…</a>…</a> — illegal HTML, producing the React hydration
//   error "<a> cannot contain a nested <a>".
//
//   Fix: render the entire modal through React Portal into document.body
//   so its DOM lives OUTSIDE the card's anchor tree. The inner <a> PDF
//   links are now siblings of <body>, not descendants of the card's <a>.
//   SSR-safe: a `mounted` gate returns null until the component is on the
//   client so createPortal is never called during server render.
//
// Two body states, both intentionally premium:
//   A) No documents uploaded yet — shown as a "مستندات التوثيق قيد
//      التفعيل" panel. Reads like a finalisation step, NEVER like a
//      missing-setup / broken feature.
//   B) Active documents exist — listed with supplier / issuer / issue
//      date / optional brand-coverage chips and a clear preview action.
// ──────────────────────────────────────────────────────
'use client';

import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import {
    listPublicTrustDocuments,
    getPublicTrustFileUrl,
    type TrustDocument,
} from '@/lib/trust-api';

interface Props {
    onClose: () => void;
}

// Summary-block bullets — the three core claims, each rendered with an
// icon. Intentionally small and scannable; this is the first thing the
// customer reads inside the modal regardless of whether documents exist.
const SUMMARY_CLAIMS: { icon: string; text: string }[] = [
    { icon: '✅', text: 'منتج أصلي 100%' },
    { icon: '🌿', text: 'موردة عبر جهات معتمدة' },
    { icon: '🔐', text: 'مضمونة المصدر' },
];

export function TrustModal({ onClose }: Props) {
    // SSR / hydration gate: createPortal(..., document.body) cannot run
    // during server render because `document` doesn't exist there. We
    // track mount on the client and defer the portal call until after the
    // first client render, matching the canonical Next.js pattern for
    // document-targeted portals.
    const [mounted, setMounted] = useState(false);
    const [docs, setDocs] = useState<TrustDocument[] | null>(null);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        // Mount gate for createPortal(..., document.body). The cascading
        // render this triggers is INTENTIONAL — it's how we flip from the
        // SSR-safe null render to the portal render. The project's
        // "react-you-might-not-need-an-effect" lint rule doesn't have a
        // carve-out for document-targeted portals, so suppress the warning
        // here with scope-explicit intent.
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setMounted(true);
    }, []);

    useEffect(() => {
        let cancelled = false;
        listPublicTrustDocuments()
            .then((items) => { if (!cancelled) setDocs(items); })
            .catch((err) => { if (!cancelled) setError((err as Error).message); });
        return () => { cancelled = true; };
    }, []);

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose]);

    // Portal gate. Must run AFTER all hooks above to respect the rules of
    // hooks — returning null on the first render is safe because the
    // effects scheduled above will still fire and flip `mounted`, causing
    // a re-render that produces the portal.
    if (!mounted) return null;

    const loading = !error && docs === null;
    const hasDocs = !error && docs !== null && docs.length > 0;
    const emptyState = !error && docs !== null && docs.length === 0;

    // QCARE-TRUST-MODAL-DARK (2026-05-12) — restyled to the QAI dark
    // theme (cyan-teal accent #7ec2ce on #11151A card over #0A0B0C
    // backdrop) so the modal reads as part of the same premium surface
    // as the mobile chat instead of a green/white "kiosk admin" overlay.
    // All structural markup, hooks, portal, RTL, and PDF link semantics
    // are unchanged — only Tailwind class strings swap. Color tokens
    // mirror lib/qai-design-system.ts and globals.css (--qai-*).
    const modalContent = (
        <div
            className="fixed inset-0 z-[200] flex items-center justify-center bg-black/70 p-4 backdrop-blur-md"
            onClick={onClose}
            role="dialog"
            aria-modal="true"
            aria-label="ضمان الأصالة"
            dir="rtl"
        >
            <div
                className="relative w-full max-w-xl max-h-[85vh] overflow-hidden rounded-2xl bg-[#11151A] border border-[rgba(126,194,206,0.24)] shadow-[0_20px_60px_-15px_rgba(0,0,0,0.7)]"
                onClick={(e) => e.stopPropagation()}
            >
                {/* ── Header ── */}
                <div className="flex items-start justify-between gap-3 border-b border-[rgba(126,194,206,0.16)] bg-gradient-to-l from-[rgba(126,194,206,0.08)] to-transparent px-5 py-4">
                    <div>
                        <h2 className="text-lg font-bold text-white flex items-center gap-2">
                            <span aria-hidden className="text-[#7ec2ce]">🔒</span> ضمان الأصالة
                        </h2>
                        <p className="mt-1 text-xs text-[#8A99A4] leading-relaxed">
                            هذا المنتج مشمول ضمن نظام ضمان الأصالة لدينا.
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="إغلاق"
                        className="shrink-0 rounded-full p-1.5 text-white/60 hover:text-white hover:bg-white/5 transition-colors"
                    >
                        ✕
                    </button>
                </div>

                {/* ── Premium Trust Summary Block (always shown) ── */}
                <div className="border-b border-[rgba(126,194,206,0.16)] bg-[rgba(126,194,206,0.04)] px-5 py-4">
                    <ul className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-3">
                        {SUMMARY_CLAIMS.map((c) => (
                            <li
                                key={c.text}
                                className="flex items-center gap-2 rounded-lg border border-[rgba(126,194,206,0.24)] bg-[rgba(126,194,206,0.06)] px-3 py-2 text-xs font-semibold text-white"
                            >
                                <span aria-hidden className="text-sm">{c.icon}</span>
                                <span>{c.text}</span>
                            </li>
                        ))}
                    </ul>
                </div>

                {/* ── Body ── */}
                <div className="overflow-y-auto max-h-[calc(85vh-232px)] px-5 py-4">
                    {error && (
                        <div className="rounded-lg bg-red-950/40 border border-red-500/40 px-3 py-2 text-sm text-red-200">
                            تعذّر تحميل الوثائق حالياً. {error}
                        </div>
                    )}

                    {loading && (
                        <div className="text-sm text-[#8A99A4] py-6 text-center">جاري التحميل…</div>
                    )}

                    {/* State A — PDFs not uploaded yet. Premium finalisation
                        panel, intentionally not an error/empty look. */}
                    {emptyState && (
                        <div className="rounded-xl border border-[rgba(126,194,206,0.24)] bg-[rgba(126,194,206,0.06)] px-4 py-4">
                            <div className="flex items-start gap-3">
                                <div
                                    aria-hidden
                                    className="shrink-0 flex h-9 w-9 items-center justify-center rounded-full bg-[#7ec2ce] text-[#0a0b0c] text-base"
                                >
                                    🔒
                                </div>
                                <div className="min-w-0">
                                    <h3 className="text-sm font-bold text-white">
                                        مستندات التوثيق قيد التفعيل
                                    </h3>
                                    <p className="mt-1 text-xs text-[#8A99A4] leading-relaxed">
                                        كل منتجاتنا أصلية 100% ومصدرها مضمون.
                                        شهادات الاعتماد الرسمية للمورّدين تظهر هنا داخل المنصة
                                        فور اكتمال تفعيلها.
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* State B — active documents. The inner <a href> PDF
                        links are safe HTML here because this entire subtree
                        is portal-rendered into document.body, outside any
                        parent <Link>/<a> that may wrap TrustBadge. */}
                    {hasDocs && (
                        <>
                            <p className="mb-3 text-xs text-[#8A99A4] leading-relaxed">
                                الوثائق التالية تدعم منظومة الأصالة المعتمدة لدينا.
                            </p>
                            <ul className="space-y-3">
                                {docs.map((d) => {
                                    const brands = Array.isArray(d.brandsCovered)
                                        ? d.brandsCovered.filter((s): s is string => typeof s === 'string' && s.length > 0)
                                        : [];
                                    return (
                                        <li
                                            key={d.id}
                                            className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-3 hover:border-[rgba(126,194,206,0.24)] hover:bg-[rgba(126,194,206,0.06)] transition-colors"
                                        >
                                            <div className="flex items-start justify-between gap-3">
                                                <div className="min-w-0 flex-1">
                                                    <div className="font-semibold text-white truncate">{d.title}</div>
                                                    <div className="mt-0.5 flex flex-wrap gap-x-2 gap-y-0.5 text-[11px] text-[#8A99A4]">
                                                        {d.supplierName && <span>المورّد: {d.supplierName}</span>}
                                                        {d.issuerName && <span>الجهة المصدرة: {d.issuerName}</span>}
                                                        {d.issuedAt && (
                                                            <span>تاريخ الإصدار: {new Date(d.issuedAt).toLocaleDateString('ar-SA')}</span>
                                                        )}
                                                    </div>
                                                    {d.descriptionAr && (
                                                        <p className="mt-1.5 text-xs text-white/80 leading-relaxed">{d.descriptionAr}</p>
                                                    )}
                                                    {brands.length > 0 && (
                                                        <ul className="mt-2 flex flex-wrap gap-1.5">
                                                            {brands.slice(0, 8).map((b) => (
                                                                <li
                                                                    key={b}
                                                                    className="rounded-full border border-[rgba(126,194,206,0.24)] bg-[rgba(126,194,206,0.06)] px-2 py-0.5 text-[10px] font-semibold text-[#7ec2ce]"
                                                                >
                                                                    {b}
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    )}
                                                </div>
                                                <a
                                                    href={getPublicTrustFileUrl(d.id)}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="shrink-0 inline-flex items-center gap-1 rounded-full bg-[#7ec2ce] text-[#0a0b0c] text-xs font-bold px-3 py-1.5 hover:bg-[#9CCFD3] transition-colors"
                                                >
                                                    عرض الوثيقة
                                                </a>
                                            </div>
                                        </li>
                                    );
                                })}
                            </ul>
                        </>
                    )}
                </div>
            </div>
        </div>
    );

    // Portal target: document.body. The modal subtree (including its
    // <a href="…pdf"> links) is now mounted OUTSIDE any parent <Link>
    // wrapping the TrustBadge trigger — eliminating the nested-anchor
    // hydration error by DOM architecture rather than by rewriting
    // interaction semantics.
    return createPortal(modalContent, document.body);
}
